/**
 * Pixel geometry for `<textarea>` when `white-space: pre-wrap` breaks the assumption
 * “one logical line == one fixed row height” (line gutter / highlight would drift otherwise).
 */

import { logicalLineRangesExclusive } from "./lineMetrics.js";
import { paddingBottomPx, paddingTopPx, resolvedLineHeightPx } from "./editorLayoutMetrics.js";

/** CamelCase keys copied from `getComputedStyle` onto the mirror element (string keys avoid `keyof CSSStyleDeclaration` symbol index issues). */
const COPY_PROPS: readonly string[] = [
  "boxSizing",
  "width",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderTopStyle",
  "borderRightStyle",
  "borderBottomStyle",
  "borderLeftStyle",
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "fontStyle",
  "fontVariant",
  "fontWeight",
  "fontStretch",
  "fontSize",
  "lineHeight",
  "fontFamily",
  "textAlign",
  "textTransform",
  "textIndent",
  "letterSpacing",
  "wordSpacing",
  "whiteSpace",
  "wordWrap",
  "overflowWrap",
  "tabSize",
  "direction",
];

/**
 * Y offset from the top of the textarea’s **content box** (inside padding) to the top of the
 * visual row that contains `offset` (0-based character index).
 */
export function textareaCaretLineTopFromContentTop(ta: HTMLTextAreaElement, offset: number): number {
  const pos = Math.max(0, Math.min(offset, ta.value.length));
  const cs = getComputedStyle(ta);
  const d = document.createElement("div");
  d.setAttribute("aria-hidden", "true");
  for (const p of COPY_PROPS) {
    const v = Reflect.get(cs, p);
    if (typeof v === "string" && v.length) {
      Reflect.set(d.style, p, v);
    }
  }
  d.style.position = "absolute";
  d.style.visibility = "hidden";
  d.style.left = "-99999px";
  d.style.top = "0";
  d.style.height = "auto";
  d.style.overflow = "visible";
  d.style.width = `${ta.clientWidth}px`;
  d.style.whiteSpace = cs.whiteSpace;
  d.textContent = ta.value.slice(0, pos);
  const marker = document.createElement("span");
  marker.textContent = ta.value.slice(pos) || "\u200b";
  d.appendChild(marker);
  document.body.appendChild(d);
  const top = marker.offsetTop;
  d.remove();
  return top;
}

/**
 * For each logical line (newline rules match `countLogicalLines`), how many visual rows the
 * line occupies under the textarea’s current width and `white-space` (typically `pre-wrap`).
 */
export function textareaLogicalLineWrapRowCounts(ta: HTMLTextAreaElement, text: string = ta.value): number[] {
  const ranges = logicalLineRangesExclusive(text);
  const cs = getComputedStyle(ta);
  const mirror = document.createElement("div");
  mirror.setAttribute("aria-hidden", "true");
  for (const p of COPY_PROPS) {
    const v = Reflect.get(cs, p);
    if (typeof v === "string" && v.length) {
      Reflect.set(mirror.style, p, v);
    }
  }
  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.left = "-99999px";
  mirror.style.top = "0";
  mirror.style.height = "auto";
  mirror.style.overflow = "hidden";
  mirror.style.width = `${ta.clientWidth}px`;
  document.body.appendChild(mirror);
  const lh = Math.max(1e-6, resolvedLineHeightPx(cs));
  const padY = paddingTopPx(cs) + paddingBottomPx(cs);
  const out: number[] = new Array(ranges.length);
  try {
    for (let i = 0; i < ranges.length; i++) {
      const { start, endExclusive } = ranges[i]!;
      if (endExclusive <= start) {
        out[i] = 1;
        continue;
      }
      const chunk = text.slice(start, endExclusive);
      mirror.textContent = chunk.length ? chunk : "\u200b";
      const inner = Math.max(0, mirror.scrollHeight - padY);
      out[i] = Math.max(1, Math.round(inner / lh + 0.22));
    }
  } finally {
    mirror.remove();
  }
  return out;
}
